// lib/takeoff/estimateReport.js
// Cost-code breakdown + the printed estimate (a clean white report opened in its own window → Print / Save as PDF).
// Pure functions: everything comes from rollup() in estimateCalc.js, so the report always matches the screen.

const esc = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
const num = (v, d = 0) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return d;
  return Math.abs(n) < 1e-9 ? 0 : n; // never print "-0"
};
const fmt0 = (v) =>
  num(v).toLocaleString("en-US", { maximumFractionDigits: 0 });
const fmt1 = (v) =>
  num(v).toLocaleString("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
const fmtQ = (v) =>
  num(v).toLocaleString("en-US", {
    maximumFractionDigits: Math.abs(num(v)) < 10 && num(v) % 1 ? 2 : 0,
  });
const $0 = (v) =>
  num(v).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
const $2 = (v) =>
  num(v).toLocaleString("en-US", { style: "currency", currency: "USD" });
const pct = (a, b) => (b > 0 ? `${((a / b) * 100).toFixed(1)}%` : "—");

// "420 SF ÷ 42 SF = 10 pcs"  /  "73 studs × 12.1′ = 885 LF" — how a row's quantity was worked out
export function rowBasis(row) {
  const it = row.item;
  const isL = it.category === "L";
  if (row.qty === null || row.qty === undefined) return row.note || "";
  const n0 = fmt0;
  const n1 = (v, d = 1) =>
    num(v).toLocaleString("en-US", {
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    });
  const adj =
    num(it.layers, 1) > 1 || num(it.wastePct) > 0 || num(it.multiplier, 1) !== 1
      ? " ×adj"
      : "";
  const tot = row.total != null ? ` = ${n0(row.total)} ${row.totalUnit}` : "";
  if (row.mode === "pieces")
    return `${n0(row.driver)} ${row.driverUnit}${adj} ÷ ${n1(row.covUsed, row.covUsed % 1 ? 2 : 0)} ${row.covUnit} = ${n0(row.pieces ?? row.qty)} pcs${tot && isL ? tot : ""}`;
  if (row.mode === "oc")
    return row.rows
      ? `${row.rows} rows × ${n0(row.driver / row.rows)} LF${adj} → ${n0(row.pieces ?? row.qty)} pcs${row.total != null ? ` = ${n0(row.total)} ${row.totalUnit}` : ""}`
      : `${n0(row.driver)} studs${adj} → ${n0(row.pieces ?? row.qty)} pcs${row.total != null ? ` × ${n1(row.covUsed, row.covUsed % 1 ? 2 : 0)}′${tot}` : ""}`;
  return `${n1(row.driver, 1)} ${row.driverUnit || ""}${adj}`;
}

// ── cost codes ───────────────────────────────────────────────────────────────
// costCodes: [{ _id, code, name }] from /company-profile/cost-codes ("09 21 16 - Gypsum Board" or code + name)
export function costCodeIndex(costCodes = []) {
  const m = new Map();
  for (const c of costCodes || []) {
    if (!c?._id) continue;
    const raw = String(c.code || "");
    const code = c.name ? raw : raw.split(" - ")[0].trim();
    const name = c.name || raw.split(" - ").slice(1).join(" - ").trim();
    m.set(String(c._id), { id: String(c._id), code, name });
  }
  return m;
}

// which cost code an estimate item belongs to: its own (set on the item), else the catalog item's, else its
// section number, else "Uncoded"
export function resolveItemCostCode(item, { ccIndex, catalogById } = {}) {
  const own = item.costCode ? ccIndex?.get(String(item.costCode)) : null;
  if (own) return own;
  const db = item.itemDatabaseId
    ? catalogById?.get(String(item.itemDatabaseId))
    : null;
  const dbId = db?.costCode?._id || db?.costCode;
  const fromDb = dbId ? ccIndex?.get(String(dbId)) : null;
  if (fromDb) return fromDb;
  // a label or section number that IS one of the company's codes → that code (so "09 21 16" groups together)
  const byCode = (txt) => {
    const t = String(txt || "")
      .trim()
      .toLowerCase();
    if (!t || !ccIndex) return null;
    for (const c of ccIndex.values()) {
      const code = String(c.code).trim().toLowerCase();
      if (
        t === code ||
        t.startsWith(`${code} `) ||
        t.startsWith(`${code}–`) ||
        t.startsWith(`${code} –`)
      )
        return c;
    }
    return null;
  };
  if (item.costCodeLabel)
    return (
      byCode(item.costCodeLabel) || {
        id: `lbl:${item.costCodeLabel}`,
        code: item.costCodeLabel,
        name: "",
      }
    );
  const sec = String(item.sectionNumber || db?.sectionNumber || "").trim();
  if (sec) return byCode(sec) || { id: `sec:${sec}`, code: sec, name: "" };
  return { id: "uncoded", code: "Uncoded", name: "Items without a cost code" };
}

/**
 * costCodeBreakdown(R, { costCodes, catalog }) → [{ id, code, name, material, labour, hours, total, price, lines[] }]
 * price = this code's share of the client price (overhead / profit / contingency / tax spread pro-rata).
 */
export function costCodeBreakdown(
  R,
  { costCodes = [], catalog = [], blocks } = {},
) {
  const ccIndex = costCodeIndex(costCodes);
  const catalogById = new Map((catalog || []).map((c) => [String(c._id), c]));
  const T = R.totals;
  const markup = T.direct > 0 ? T.price / T.direct : 1;
  const map = new Map();
  for (const b of blocks || R.blocks) {
    for (const r of b.rows) {
      if (r.qty == null) continue;
      const cc = resolveItemCostCode(r.item, { ccIndex, catalogById });
      const g = map.get(cc.id) || {
        ...cc,
        material: 0,
        labour: 0,
        hours: 0,
        total: 0,
        lines: [],
      };
      const isL = r.item.category === "L";
      const amt = num(r.amount);
      if (isL) {
        g.labour += amt;
        g.hours += num(r.hours);
      } else g.material += amt;
      g.total += amt;
      g.lines.push({
        assembly: b.cond.name,
        area: b.area || "",
        ref: r.item.ref,
        itemCode: r.item.itemCode,
        title: r.item.title,
        category: isL ? "L" : "M",
        qty: Math.abs(r.qty) < 1e-9 ? 0 : r.qty,
        unit: r.unit,
        rate: num(r.item.rate),
        hours: num(r.hours),
        amount: amt,
      });
      map.set(cc.id, g);
    }
  }
  const out = [...map.values()].map((g) => ({ ...g, price: g.total * markup }));
  out.sort((a, b) =>
    a.id === "uncoded"
      ? 1
      : b.id === "uncoded"
        ? -1
        : String(a.code).localeCompare(String(b.code), undefined, {
            numeric: true,
          }),
  );
  return out;
}

// ── the printed report ───────────────────────────────────────────────────────
export const PRINT_SECTIONS = [
  ["summary", "Summary & price build-up"],
  ["costcodes", "Cost code breakdown"],
  ["areas", "By bid area"],
  ["assemblies", "Assemblies (items, quantities, basis)"],
  ["legend", "Sheet legend (drawing + assemblies used on each sheet)"],
  ["labour", "Labour hours & crew days"],
  ["takelist", "Material take-list"],
  ["notes", "Scope, inclusions & exclusions"],
  ["signatures", "Sign-off"],
];
export const PRINT_PRESETS = {
  internal: {
    label: "Internal estimate",
    sections: PRINT_SECTIONS.map(([k]) => k),
    showRates: true,
  },
  client: {
    label: "Client proposal",
    sections: [
      "summary",
      "areas",
      "assemblies",
      "legend",
      "notes",
      "signatures",
    ],
    showRates: false,
  },
};
/**
 * buildReportHtml({ R, breakdown, legends, settings, meta, options }) → full HTML document
 *   meta:    { company, client, project, drawing, estimateNo, preparedBy, ownerKind, status, logoUrl }
 *   options: { mode: "internal"|"client", sections: [..], showRates, inclusions, exclusions, assumptions, validDays }
 *   legends: from buildSheetLegends(), each optionally with .img (data URL of the sheet with assemblies drawn on it)
 */
export function buildReportHtml({
  R,
  breakdown = [],
  settings = {},
  meta = {},
  options = {},
  blocks: blocksIn,
  legends = [],
}) {
  const T = R.totals;
  const S = R.settings;
  const blocks = blocksIn || R.blocks;
  const client = options.mode === "client";
  const show = new Set(
    options.sections || PRINT_PRESETS[client ? "client" : "internal"].sections,
  );
  const rates = options.showRates ?? !client;
  const markup = T.direct > 0 ? T.price / T.direct : 1;
  const today = new Date();
  const dateStr = today.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const validDays = num(options.validDays, 30);
  const validTo = new Date(
    today.getTime() + validDays * 86400000,
  ).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const title = client ? "Proposal" : "Estimate";
  const lines = (txt) =>
    String(txt || "")
      .split(/\r?\n/)
      .map((l) => l.replace(/^[\s•·\-–—*]+/, "").trim())
      .filter(Boolean);

  const kpi = (label, value, sub = "") =>
    `<div class="kpi"><div class="kl">${esc(label)}</div><div class="kv">${value}</div>${sub ? `<div class="ks">${sub}</div>` : ""}</div>`;

  const parts = [];

  // header
  parts.push(`
  <header class="top">
    <div>
      ${meta.logoUrl ? `<img src="${esc(meta.logoUrl)}" class="logo" alt="">` : ""}
      <div class="co">${esc(meta.company || "")}</div>
      ${meta.companyLine ? `<div class="muted">${esc(meta.companyLine)}</div>` : ""}
    </div>
    <div class="doc">
      <div class="doct">${title}</div>
      <table class="meta">
        ${meta.estimateNo ? `<tr><td>No.</td><td>${esc(meta.estimateNo)}</td></tr>` : ""}
        <tr><td>Date</td><td>${esc(dateStr)}</td></tr>
        ${client ? `<tr><td>Valid until</td><td>${esc(validTo)}</td></tr>` : `<tr><td>Status</td><td>${esc(meta.status || "draft")}</td></tr>`}
        ${meta.preparedBy ? `<tr><td>Prepared by</td><td>${esc(meta.preparedBy)}</td></tr>` : ""}
      </table>
    </div>
  </header>
  <section class="proj">
    <div><div class="kl">${meta.ownerKind === "opportunity" ? "Opportunity" : "Project"}</div><div class="pv">${esc(meta.project || "—")}</div></div>
    ${meta.client ? `<div><div class="kl">Client</div><div class="pv">${esc(meta.client)}</div></div>` : ""}
    <div><div class="kl">Drawings</div><div class="pv">${esc(meta.drawing || "—")}</div></div>
    <div><div class="kl">Scope</div><div class="pv">${blocks.length} assembl${blocks.length === 1 ? "y" : "ies"}${R.byArea.length > 1 ? ` · ${R.byArea.length} bid areas` : ""}</div></div>
  </section>`);

  // summary
  if (show.has("summary")) {
    parts.push(`<h2>Summary</h2>
    <div class="kpis">
      ${kpi(client ? "Total price" : "Price to client", `<span class="accent">${$0(T.price)}</span>`, S.taxPct ? `incl. ${S.taxPct}% tax` : "")}
      ${client ? "" : kpi("Direct cost", $0(T.direct), `Material ${pct(T.material, T.direct)} · Labour ${pct(T.labour, T.direct)}`)}
      ${client ? "" : kpi("Margin (OH + profit)", $0(T.overhead + T.profit), pct(T.overhead + T.profit, T.price))}
      ${kpi("Labour", `${fmt0(T.hours)} h`, `${fmt1(T.days)} crew-days · ${S.crewSize} × ${S.hoursPerDay} h`)}
    </div>`);
    if (!client) {
      const row = (l, v, p, strong) =>
        `<tr class="${strong ? "strong" : ""}"><td>${esc(l)}</td><td class="r muted">${p ?? ""}</td><td class="r">${$2(v)}</td></tr>`;
      parts.push(`<table class="build">
        ${row("Material", T.material)}
        ${row("Labour", T.labour)}
        ${row("Direct cost", T.direct, null, true)}
        ${row("Overhead", T.overhead, `${S.overheadPct}% of direct`)}
        ${row("Profit", T.profit, `${S.profitPct}% of cost + OH`)}
        ${row("Contingency", T.contingency, `${S.contingencyPct}% of direct`)}
        ${row("Subtotal", T.subtotal, null, true)}
        ${row("Tax", T.tax, `${S.taxPct}%`)}
        <tr class="grand"><td>Price to client</td><td></td><td class="r">${$2(T.price)}</td></tr>
      </table>`);
    } else {
      parts.push(`<table class="build">
        <tr><td>Subtotal</td><td></td><td class="r">${$2(T.subtotal)}</td></tr>
        <tr><td>Tax</td><td class="r muted">${S.taxPct}%</td><td class="r">${$2(T.tax)}</td></tr>
        <tr class="grand"><td>Total</td><td></td><td class="r">${$2(T.price)}</td></tr>
      </table>`);
    }
  }

  // cost codes
  if (show.has("costcodes") && breakdown.length) {
    const tot = breakdown.reduce((s, g) => s + g.total, 0);
    parts.push(`<h2>Cost code breakdown</h2>
    <table class="grid">
      <thead><tr><th>Cost code</th><th>Description</th><th class="r">Material</th><th class="r">Labour</th><th class="r">Hours</th><th class="r">Direct</th><th class="r">% </th><th class="r">Price</th></tr></thead>
      <tbody>
      ${breakdown
        .map(
          (g) =>
            `<tr><td class="mono">${esc(g.code)}</td><td>${esc(g.name || "")}</td><td class="r">${$0(g.material)}</td><td class="r">${$0(g.labour)}</td><td class="r">${fmt0(g.hours)}</td><td class="r">${$0(g.total)}</td><td class="r muted">${pct(g.total, tot)}</td><td class="r b">${$0(g.price)}</td></tr>`,
        )
        .join("")}
      </tbody>
      <tfoot><tr><td colspan="2">Total</td><td class="r">${$0(T.material)}</td><td class="r">${$0(T.labour)}</td><td class="r">${fmt0(T.hours)}</td><td class="r">${$0(T.direct)}</td><td></td><td class="r">${$0(T.price)}</td></tr></tfoot>
    </table>`);
  }

  // bid areas
  if (
    show.has("areas") &&
    (R.byArea.length > 1 || (R.byArea[0] && R.byArea[0].area !== "Unassigned"))
  ) {
    parts.push(`<h2>By bid area</h2>
    <table class="grid">
      <thead><tr><th>Bid area</th>${client ? "" : `<th class="r">Material</th><th class="r">Labour</th><th class="r">Hours</th><th class="r">Direct</th>`}<th class="r">Price</th></tr></thead>
      <tbody>${R.byArea
        .map(
          (a) =>
            `<tr><td>${esc(a.area)}</td>${client ? "" : `<td class="r">${$0(a.material)}</td><td class="r">${$0(a.labour)}</td><td class="r">${fmt0(a.hours)}</td><td class="r">${$0(a.total)}</td>`}<td class="r b">${$0(a.total * markup)}</td></tr>`,
        )
        .join("")}</tbody>
    </table>`);
  }

  // assemblies
  if (show.has("assemblies")) {
    parts.push(`<h2>${client ? "Scope by assembly" : "Assemblies"}</h2>`);
    if (client) {
      parts.push(`<table class="grid">
        <thead><tr><th>Assembly</th><th>Area</th><th class="r">Quantity</th><th>Includes</th><th class="r">Price</th></tr></thead>
        <tbody>${blocks
          .filter((b) => b.rows.length)
          .map((b) => {
            const m = b.metrics || {};
            const q = [
              m.sf > 0 && `${fmt0(m.sf)} SF`,
              m.lf > 0 && `${fmt0(m.lf)} LF`,
              m.count > 0 && `${fmt0(m.count)} EA`,
            ]
              .filter(Boolean)
              .join(" · ");
            const inc = b.rows
              .filter((r) => r.item.category !== "L")
              .map((r) => esc(r.item.title || r.item.itemCode));
            return `<tr><td class="b">${esc(b.cond.name)}${b.height ? `<div class="muted">H ${fmt1(b.height)}′</div>` : ""}${b.mapping?.notes ? `<div class="note">${esc(b.mapping.notes)}</div>` : ""}</td><td>${esc(b.area || "")}</td><td class="r">${q || "—"}</td><td class="small">${inc.join(" · ") || "—"}</td><td class="r b">${$0(b.total * markup)}</td></tr>`;
          })
          .join("")}</tbody>
      </table>`);
    } else {
      for (const b of blocks) {
        const m = b.metrics || {};
        const q = [
          m.sf > 0 && `${fmt0(m.sf)} SF`,
          m.lf > 0 && `${fmt0(m.lf)} LF`,
          m.count > 0 && `${fmt0(m.count)} EA`,
        ]
          .filter(Boolean)
          .join(" · ");
        parts.push(`<div class="asm">
          <div class="asmh">
            <span class="dot" style="background:${esc(b.cond.color || "#999")}"></span>
            <span class="asmn">${esc(b.cond.name)}</span>
            ${b.area ? `<span class="tag">${esc(b.area)}</span>` : ""}
            ${b.height ? `<span class="tag">H ${fmt1(b.height)}′</span>` : ""}
            <span class="muted">${q || "no quantity"}</span>
            <span class="asmt">${$0(b.total)}</span>
          </div>
          ${
            b.rows.length
              ? `<table class="grid tight">
            <thead><tr><th>#</th><th>Code</th><th>Item</th><th>Basis</th><th class="r">Waste</th><th class="r">Qty</th><th>Unit</th>${rates ? `<th class="r">Rate</th>` : ""}<th class="r">Hours</th><th class="r">Amount</th></tr></thead>
            <tbody>${b.rows
              .map((r) => {
                const it = r.item;
                const isL = it.category === "L";
                return `<tr><td class="muted mono">${it.ref != null ? String(it.ref).padStart(3, "0") : ""}</td><td class="mono">${esc(it.itemCode)}</td><td>${isL ? '<span class="lb">L</span> ' : ""}${esc(it.title || "")}${it.size ? ` <span class="muted">· ${esc(it.size)}</span>` : ""}</td><td class="small muted">${esc(rowBasis(r))}</td><td class="r muted">${num(it.wastePct) ? `${num(it.wastePct)}%` : ""}</td><td class="r">${r.qty == null ? "—" : fmtQ(r.qty)}</td><td>${esc(r.unit || "")}</td>${rates ? `<td class="r">${$2(it.rate)}</td>` : ""}<td class="r">${isL && r.hours ? fmt1(r.hours) : ""}</td><td class="r b">${r.amount == null ? "—" : $0(r.amount)}</td></tr>`;
              })
              .join("")}</tbody>
            <tfoot><tr><td colspan="${rates ? 8 : 7}">Material ${$0(b.material)} · Labour ${$0(b.labour)}</td><td class="r">${fmt1(b.hours)}</td><td class="r">${$0(b.total)}</td></tr></tfoot>
          </table>`
              : `<div class="muted small pad">Not priced yet.</div>`
          }
          ${b.mapping?.notes ? `<div class="note">${esc(b.mapping.notes)}</div>` : ""}
        </div>`);
      }
    }
  }

  // sheet legend: one page per drawing sheet — the sheet with its assemblies drawn on it, then the key
  if (show.has("legend") && legends.length) {
    const TYPE = { linear: "Walls", area: "Areas", count: "Counts" };
    parts.push(`<h2>Sheet legend — assemblies by sheet</h2>
      <p class="small muted">Each assembly is drawn on its sheet in the colour shown in the key.
      <span class="kw" style="background:#22d3ee"></span> windows deducted ·
      <span class="kw" style="background:#e879f9"></span> doors deducted · dots are counts.</p>`);
    for (const s of legends) {
      let lastType = null;
      const rows = s.rows
        .map((r) => {
          const head =
            r.type !== lastType
              ? `<tr class="lgrp"><td colspan="4">${TYPE[r.type] || ""}</td></tr>`
              : "";
          lastType = r.type;
          const tag =
            r.cond.wallType && !String(r.cond.wallType).startsWith("__")
              ? String(r.cond.wallType).replace(/·WR$/, "")
              : "";
          return `${head}<tr>
            <td class="lsw"><span style="background:${esc(r.cond.color || "#999")}"></span></td>
            <td>
              <div class="b">${tag ? `<span class="mono lt">${esc(tag)}</span> ` : ""}${esc(r.cond.name)}</div>
              ${r.desc.length ? `<ul class="ld">${r.desc.map((d) => `<li>${esc(d)}</li>`).join("")}</ul>` : ""}
              ${!client && r.items.length ? `<div class="small muted"><b>Priced items:</b> ${r.items.map(esc).join(" · ")}</div>` : ""}
            </td>
            <td class="r muted">${esc(r.height)}</td>
            <td class="r b">${esc(r.qty)}</td>
          </tr>`;
        })
        .join("");
      parts.push(`<div class="legsheet">
        <div class="lsh"><span class="lsn">${esc(s.label)}</span>${s.area ? `<span class="tag">${esc(s.area)}</span>` : ""}<span class="muted small" style="margin-left:auto">Sheet ${s.page}</span></div>
         ${s.img ? `<img class="legimg" src="${s.img}" alt="${esc(s.label)}">` : ""}        <table class="grid">
          <thead><tr><th></th><th>Assembly</th><th class="r">Height</th><th class="r">Quantity on this sheet</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`);
    }
  }

  // labour
  if (show.has("labour") && T.hours > 0) {
    const lab = new Map();
    for (const b of blocks)
      for (const r of b.rows) {
        if (r.item.category !== "L" || r.qty == null) continue;
        const k = r.item.itemCode || r.item.title;
        const g = lab.get(k) || {
          code: r.item.itemCode,
          title: r.item.title,
          unit: r.unit,
          qty: 0,
          hours: 0,
          amount: 0,
          prod: r.item.productionRate,
        };
        g.qty += num(r.qty);
        g.hours += num(r.hours);
        g.amount += num(r.amount);
        lab.set(k, g);
      }
    parts.push(`<h2>Labour</h2>
    <table class="grid">
      <thead><tr><th>Code</th><th>Task</th><th class="r">Quantity</th><th>Unit</th><th class="r">Production</th><th class="r">Hours</th><th class="r">Crew-days</th>${rates ? `<th class="r">Amount</th>` : ""}</tr></thead>
      <tbody>${[...lab.values()]
        .sort((a, b) => b.hours - a.hours)
        .map(
          (g) =>
            `<tr><td class="mono">${esc(g.code)}</td><td>${esc(g.title)}</td><td class="r">${fmt0(g.qty)}</td><td>${esc(g.unit)}</td><td class="r muted">${num(g.prod) ? `${esc(g.prod)}/h` : "—"}</td><td class="r">${fmt1(g.hours)}</td><td class="r">${S.crewSize > 0 && S.hoursPerDay > 0 ? fmt1(g.hours / (S.crewSize * S.hoursPerDay)) : "—"}</td>${rates ? `<td class="r">${$0(g.amount)}</td>` : ""}</tr>`,
        )
        .join("")}</tbody>
      <tfoot><tr><td colspan="5">Total · crew of ${S.crewSize} × ${S.hoursPerDay} h/day</td><td class="r">${fmt1(T.hours)}</td><td class="r">${fmt1(T.days)}</td>${rates ? `<td class="r">${$0(T.labour)}</td>` : ""}</tr></tfoot>
    </table>`);
  }

  // take-list
  if (show.has("takelist") && R.takeList.length) {
    parts.push(`<h2>Material take-list</h2>
    <table class="grid">
      <thead><tr><th>Code</th><th>Material</th><th>Size</th><th class="r">Qty</th><th>Unit</th>${rates ? `<th class="r">Cost</th>` : ""}<th>Used in</th></tr></thead>
      <tbody>${R.takeList
        .map(
          (t) =>
            `<tr><td class="mono">${esc(t.itemCode)}</td><td>${esc(t.title)}</td><td>${esc(t.size || "")}</td><td class="r b">${fmt0(t.qty)}</td><td>${esc(t.unit)}</td>${rates ? `<td class="r">${$0(t.amount)}</td>` : ""}<td class="small muted">${esc(t.assemblies.join(", "))}</td></tr>`,
        )
        .join("")}</tbody>
    </table>`);
  }

  // notes
  if (show.has("notes")) {
    const scope = blocks
      .filter((b) => b.mapping?.notes)
      .map(
        (b) => `<li><b>${esc(b.cond.name)}:</b> ${esc(b.mapping.notes)}</li>`,
      );
    const box = (h, txt) => {
      const l = lines(txt);
      return l.length
        ? `<div class="nbox"><h3>${esc(h)}</h3><ul>${l.map((x) => `<li>${esc(x)}</li>`).join("")}</ul></div>`
        : "";
    };
    const html = [
      scope.length && !client
        ? `<div class="nbox"><h3>Scope notes</h3><ul>${scope.join("")}</ul></div>`
        : "",
      box("Inclusions", options.inclusions),
      box("Exclusions", options.exclusions),
      box("Assumptions & qualifications", options.assumptions),
    ].join("");
    if (html.trim())
      parts.push(
        `<h2>Scope, inclusions & exclusions</h2><div class="notes">${html}</div>`,
      );
    if (client)
      parts.push(
        `<p class="small muted">Prices valid for ${validDays} days (until ${esc(validTo)}). Quantities are taken from the drawings listed above; changes to the drawings or scope will be priced separately.</p>`,
      );
  }

  // sign-off
  if (show.has("signatures")) {
    const sig = (l) =>
      `<div class="sig"><div class="line"></div><div class="small muted">${esc(l)}</div><div class="line short"></div><div class="small muted">Date</div></div>`;
    parts.push(
      `<div class="sigs">${client ? sig(`Accepted for ${meta.client || "the client"}`) + sig(`For ${meta.company || "the contractor"}`) : sig(`Prepared by${meta.preparedBy ? ` — ${meta.preparedBy}` : ""}`) + sig("Reviewed / approved by")}</div>`,
    );
  }

  const footer = `${esc(meta.company || "")} · ${title}${meta.estimateNo ? ` ${esc(meta.estimateNo)}` : ""} · ${esc(meta.project || "")}`;
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)} — ${esc(meta.project || "")}</title>
<style>
  @page { size: letter; margin: 14mm 12mm 16mm;
    @bottom-left { content: "${footer.replace(/"/g, "'")}"; font: 8pt Inter, Arial, sans-serif; color: #888; }
    @bottom-right { content: "Page " counter(page) " of " counter(pages); font: 8pt Inter, Arial, sans-serif; color: #888; } }
  * { box-sizing: border-box; }
  body { font-family: Inter, "Segoe UI", Arial, sans-serif; color: #1f2328; font-size: 10pt; margin: 0; background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .page { max-width: 8.1in; margin: 0 auto; padding: 0.3in 0.1in; }
  .top { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #f97316; padding-bottom: 12px; }
  .logo { max-height: 46px; margin-bottom: 6px; }
  .co { font-size: 16pt; font-weight: 700; }
  .doc { text-align: right; }
  .doct { font-size: 22pt; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; color: #f97316; }
  .meta { margin-left: auto; border-collapse: collapse; font-size: 9pt; }
  .meta td { padding: 1px 0 1px 12px; } .meta td:first-child { color: #777; }
  .proj { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; background: #f6f7f9; border-radius: 8px; padding: 10px 12px; margin: 12px 0 4px; }
  .pv { font-weight: 600; font-size: 10pt; }
  h2 { font-size: 11.5pt; margin: 18px 0 8px; padding-bottom: 4px; border-bottom: 1px solid #e3e5e8; text-transform: uppercase; letter-spacing: .06em; color: #333; break-after: avoid; }
  h3 { font-size: 10pt; margin: 0 0 4px; }
  .kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 10px; }
  .kpi { border: 1px solid #e3e5e8; border-radius: 8px; padding: 8px 10px; }
  .kl { font-size: 7.5pt; text-transform: uppercase; letter-spacing: .08em; color: #888; }
  .kv { font-size: 15pt; font-weight: 700; margin-top: 2px; } .ks { font-size: 8pt; color: #777; }
  .accent { color: #ea580c; }
  table { width: 100%; border-collapse: collapse; }
  .build { max-width: 4.6in; margin-left: auto; }
  .build td { padding: 3px 6px; border-bottom: 1px solid #f0f1f3; }
  .build .strong td { font-weight: 700; border-top: 1px solid #ccc; }
  .build .grand td { font-weight: 800; font-size: 12pt; background: #fff4ec; color: #c2410c; border: none; padding: 7px 6px; }
  .grid th { text-align: left; font-size: 7.5pt; text-transform: uppercase; letter-spacing: .06em; color: #666; background: #f6f7f9; padding: 5px 6px; border-bottom: 1px solid #dfe2e6; }
  .grid td { padding: 4px 6px; border-bottom: 1px solid #f0f1f3; vertical-align: top; }
  .grid tfoot td { font-weight: 700; border-top: 1.5px solid #bbb; border-bottom: none; background: #fafbfc; }
  .grid tr { break-inside: avoid; }
  .tight td { padding: 3px 5px; font-size: 9pt; }
  .r { text-align: right; white-space: nowrap; } .b { font-weight: 600; } .mono { font-family: "SF Mono", Menlo, Consolas, monospace; font-size: 8.5pt; white-space: nowrap; }
  .muted { color: #888; } .small { font-size: 8.5pt; } .pad { padding: 6px 4px; }
  .asm { border: 1px solid #e3e5e8; border-radius: 8px; margin: 0 0 10px; overflow: hidden; break-inside: avoid-page; }
  .asmh { display: flex; align-items: center; gap: 8px; padding: 7px 10px; background: #fafbfc; border-bottom: 1px solid #e3e5e8; }
  .asmn { font-weight: 700; } .asmt { margin-left: auto; font-weight: 700; }
  .dot { width: 9px; height: 9px; border-radius: 50%; display: inline-block; }
  .tag { font-size: 8pt; background: #fff1e6; color: #c2410c; border-radius: 4px; padding: 1px 6px; }
  .lb { font-size: 7pt; background: #e0f2fe; color: #0369a1; border-radius: 3px; padding: 0 4px; font-weight: 700; }
  .note { font-size: 8.5pt; color: #555; background: #fffbeb; border-left: 3px solid #f59e0b; padding: 5px 8px; margin: 6px 8px 8px; }
  .notes { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .nbox { border: 1px solid #e3e5e8; border-radius: 8px; padding: 8px 12px; break-inside: avoid; }
  .nbox ul { margin: 0; padding-left: 16px; } .nbox li { margin: 2px 0; }
  .sigs { display: flex; gap: 40px; margin-top: 36px; break-inside: avoid; }
  .sig { flex: 1; } .line { border-bottom: 1px solid #333; height: 34px; } .line.short { width: 50%; height: 26px; }
  .legsheet { margin: 0 0 14px; }
  .legsheet + .legsheet { break-before: page; }
  .lsh { display: flex; align-items: baseline; gap: 8px; margin-bottom: 4px; }
  .lsn { font-size: 11pt; font-weight: 700; }
  .legimg { display: block; width: 100%; max-height: 5.6in; object-fit: contain; border: 1px solid #d9dce0; border-radius: 6px; margin: 0 0 8px; background: #fff; }
  .kw { display: inline-block; width: 18px; height: 6px; border-radius: 2px; vertical-align: middle; }
  .lgrp td { background: #f3f4f6; font-weight: 700; font-size: 7.5pt; text-transform: uppercase; letter-spacing: .06em; color: #555; }
  .lsw { width: 22px; }
  .lsw span { display: block; width: 13px; height: 13px; border-radius: 50%; border: 1px solid rgba(0,0,0,.35); }
  .lt { background: #eef0f2; border-radius: 3px; padding: 0 4px; }
  .ld { margin: 2px 0 0 14px; padding: 0; font-size: 8.5pt; color: #555; }
  .toolbar { position: sticky; top: 0; background: #111827; color: #fff; padding: 8px 14px; display: flex; gap: 8px; align-items: center; font-size: 10pt; }
  .toolbar button { background: #f97316; color: #fff; border: 0; border-radius: 6px; padding: 6px 14px; font-weight: 600; cursor: pointer; }
  @media print { .toolbar { display: none; } .page { padding: 0; max-width: none; } }
</style></head><body>
<div class="toolbar"><span>${esc(title)} preview — use “Save as PDF” in the print dialog for a PDF.</span><span style="flex:1"></span><button onclick="window.print()">Print / Save PDF</button></div>
<div class="page">${parts.join("\n")}</div>
</body></html>`;
}

// open the report in a new window and bring up the print dialog
// open the report window straight away (call this inside the click), fill it when the report is ready
// open the report window straight away (call this inside the click) with an animated loading screen
export function openReportWindow() {
  const w = window.open("", "_blank");
  if (!w) return null;
  w.document.open();
  w.document
    .write(`<!doctype html><html><head><meta charset="utf-8"><title>Preparing report…</title>
<style>
  *{box-sizing:border-box}
  body{margin:0;min-height:100vh;display:grid;place-items:center;background:#0b1220;font-family:Inter,"Segoe UI",Arial,sans-serif;color:#e5e7eb}
  .card{width:min(440px,90vw);padding:32px;border-radius:20px;background:#111827;border:1px solid rgba(255,255,255,.08);box-shadow:0 20px 60px rgba(0,0,0,.5);text-align:center}
  .spin{width:68px;height:68px;margin:0 auto 22px;position:relative}
  .spin i{position:absolute;inset:0;border-radius:50%;border:4px solid transparent;border-top-color:#f97316;animation:r 1s linear infinite}
  .spin i:nth-child(2){inset:9px;border-top-color:#38bdf8;animation-duration:1.5s;animation-direction:reverse}
  .spin i:nth-child(3){inset:18px;border-top-color:#fdba74;animation-duration:.8s}
  @keyframes r{to{transform:rotate(360deg)}}
  h1{font-size:17px;margin:0 0 6px;font-weight:700}
  p{margin:0;font-size:13px;color:#9ca3af;min-height:18px}
  .bar{margin-top:22px;height:8px;border-radius:99px;background:#1f2937;overflow:hidden}
  .fill{height:100%;width:6%;border-radius:99px;background:linear-gradient(90deg,#f97316,#fb923c);transition:width .6s ease;position:relative;overflow:hidden}
  .fill:after{content:"";position:absolute;inset:0;background:linear-gradient(90deg,transparent,rgba(255,255,255,.5),transparent);transform:translateX(-100%);animation:s 1.3s ease-in-out infinite}
  @keyframes s{to{transform:translateX(100%)}}
  .pct{margin-top:8px;font-size:11px;color:#6b7280;font-variant-numeric:tabular-nums}
  .dots span{animation:b 1.2s infinite;opacity:.2}
  .dots span:nth-child(2){animation-delay:.2s}.dots span:nth-child(3){animation-delay:.4s}
  @keyframes b{50%{opacity:1}}
</style></head><body>
<div class="card">
  <div class="spin"><i></i><i></i><i></i></div>
  <h1 id="t">Preparing your report<span class="dots"><span>.</span><span>.</span><span>.</span></span></h1>
  <p id="d">Getting everything together</p>
  <div class="bar"><div class="fill" id="f"></div></div>
  <div class="pct" id="p">Starting…</div>
</div></body></html>`);
  w.document.close();
  return w;
}

// update the loading screen: reportWindowStatus(win, "Drawing sheet 2 of 4", 45, "A-101 — Level 1 Plan")
export function reportWindowStatus(w, title, pct = null, detail = "") {
  try {
    const d = w.document;
    const set = (id, v) => {
      const el = d.getElementById(id);
      if (el) el.textContent = v;
    };
    set("t", title);
    set("d", detail || "This can take a moment for large drawings");
    if (pct != null) {
      const v = Math.max(4, Math.min(100, Math.round(pct)));
      const f = d.getElementById("f");
      if (f) f.style.width = `${v}%`;
      set("p", `${v}%`);
    }
  } catch {
    /* window closed or already replaced by the report */
  }
}
export function fillReportWindow(w, html) {
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();
  // let the sheet pictures load before the print dialog comes up
  setTimeout(() => {
    try {
      w.print();
    } catch {
      /* the preview stays open with its own Print button */
    }
  }, 900);
}

// open the report in a new window and bring up the print dialog (everything already built)
export function openPrintWindow(html) {
  const w = openReportWindow();
  if (!w) return false;
  fillReportWindow(w, html);
  return true;
}
